// src/components/settings/PwdChange.jsx
import React, { useCallback, useMemo, useState } from "react";
import axios from "axios";
import {
  RESOLVE_LOCAL_AUTH_STATE,
  RESOLVE_LOCAL_USER_STATE,
} from "../auth/AuthActions";
import Config from "../../config/Config.js";

/* -------------------------
   Reusable Password Field
------------------------- */
function PasswordField({
  id,
  label,
  value,
  onChange,
  disabled,
  autoComplete,
}) {
  const [visible, setVisible] = useState(false);
  const toggle = useCallback(() => setVisible((v) => !v), []);

  return (
    <div className="form-group">
      <label htmlFor={id} className="form-label">
        {label}
      </label>

      <div className="pw-field">
        <input
          id={id}
          type={visible ? "text" : "password"}
          className="form-control auth-form"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          disabled={disabled}
          autoComplete={autoComplete}
          aria-describedby={`${id}-hint`}
        />
        <button
          type="button"
          className="pw-toggle"
          onClick={toggle}
          aria-label={visible ? "Hide password" : "Show password"}
          aria-pressed={visible}
        >
          {visible ? "Hide" : "Show"}
        </button>
      </div>
    </div>
  );
}

/* -------------------------
   Main Component
------------------------- */
export default function PwdChange() {
  const auth = useMemo(() => RESOLVE_LOCAL_AUTH_STATE(), []);
  useMemo(() => RESOLVE_LOCAL_USER_STATE(), []); // kept if you rely on side effects elsewhere

  const [currentPw, setCurrentPw] = useState("");
  const [newPw1, setNewPw1] = useState("");
  const [newPw2, setNewPw2] = useState("");

  const [loading, setLoading] = useState(false);
  const [feedback, setFeedback] = useState({ type: "", message: "" });

  // Validators (conservative: same rules, centralized)
  const MIN_LEN = 8;
  const meetsMinLen = newPw1.length >= MIN_LEN;
  const notSameAsOld = !!newPw1 && !!currentPw && newPw1 !== currentPw;
  const pwMatch = !!newPw1 && !!newPw2 && newPw1 === newPw2;

  const canSubmit =
    !!currentPw && !!newPw1 && !!newPw2 && meetsMinLen && pwMatch && notSameAsOld;

  const setError = useCallback((msg) => {
    setFeedback({ type: "error", message: msg });
  }, []);
  const setSuccess = useCallback((msg) => {
    setFeedback({ type: "success", message: msg });
  }, []);

  const handleSubmit = useCallback(
    async (e) => {
      e.preventDefault();
      setFeedback({ type: "", message: "" });

      if (!canSubmit) {
        setError("Fix the errors before submitting.");
        return;
      }

      try {
        setLoading(true);
        await axios.post(
          `${Config().pwdChangeUrl}`,
          { new_password1: newPw1, new_password2: newPw2 },
          {
            auth: {
              username: auth?.username,
              password: currentPw,
            },
          }
        );

        setSuccess("Password updated successfully.");
        setCurrentPw("");
        setNewPw1("");
        setNewPw2("");
      } catch (err) {
        console.error('Password Change Error:', err);
        console.error('Password Change Error Response:', err.response);
        console.error('Password Change Error Status:', err.response?.status);
        console.error('Password Change Error Data:', err.response?.data);
        
        let serverMsg = "Could not update password. Check your current password and try again.";
        if (err?.response?.status === 400) {
          serverMsg = err?.response?.data?.detail || err?.response?.data?.error || "Invalid password data provided.";
        } else if (err?.response?.status === 403) {
          serverMsg = "Current password is incorrect.";
        } else if (err?.response?.status === 500) {
          serverMsg = "Server error occurred while changing password. Please try again later.";
        } else if (err?.response?.data?.detail) {
          serverMsg = err.response.data.detail;
        } else if (err?.response?.data?.error) {
          serverMsg = err.response.data.error;
        }
        setError(String(serverMsg));
      } finally {
        setLoading(false);
      }
    },
    [auth?.username, currentPw, newPw1, newPw2, canSubmit, setError, setSuccess]
  );

  return (
    <div className="settings__card">
      <h2 className="settings__formTitle">Password</h2>
      <p className="settings__formSubtitle">
        Enter your current password and choose a new one.
      </p>
      <hr />

      {/* Feedback */}
      {!!feedback.message && (
        <div
          role="status"
          aria-live="polite"
          className={
            feedback.type === "success"
              ? "form-alert form-alert--success"
              : "form-alert form-alert--error"
          }
        >
          {feedback.message}
        </div>
      )}

      <form onSubmit={handleSubmit} className="settings__form" noValidate>
        {/* Current */}
        <PasswordField
          id="current_password"
          label="Current password"
          value={currentPw}
          onChange={setCurrentPw}
          disabled={loading}
          autoComplete="current-password"
        />

        {/* New */}
        <div>
          <PasswordField
            id="new_password1"
            label="New password"
            value={newPw1}
            onChange={setNewPw1}
            disabled={loading}
            autoComplete="new-password"
          />
          <small id="new_password1-hint" className="field-hint">
            At least {MIN_LEN} characters. Avoid reusing old passwords.
          </small>

          {!meetsMinLen && newPw1 && (
            <small className="field-hint field-hint--error">
              Must be at least {MIN_LEN} characters.
            </small>
          )}
          {currentPw && newPw1 && currentPw === newPw1 && (
            <small className="field-hint field-hint--error">
              New password must be different from the current password.
            </small>
          )}
        </div>

        {/* Confirm */}
        <div>
          <PasswordField
            id="new_password2"
            label="Confirm new password"
            value={newPw2}
            onChange={setNewPw2}
            disabled={loading}
            autoComplete="new-password"
          />
          {newPw2 && newPw1 && !pwMatch && (
            <small className="field-hint field-hint--error">
              Passwords do not match.
            </small>
          )}
        </div>

        <div className="form-actions">
          <button
            type="submit"
            className="btn btn-brand"
            disabled={loading || !canSubmit}
          >
            {loading ? "Updating…" : "Change password"}
          </button>
        </div>
      </form>
    </div>
  );
}
